<template>
    <div>
        <div
            class="map"
            :id="mapName"
            :class="{
                'map--event': parent === 'event',
                'map--shared': parent === 'shared'
            }"
        >
            <slot></slot>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

export default {
    name: 'HereMap',
    props: {
        padding: {
            required: false,
            type: Object,
            default: () => {
                return {
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                }
            }
        },
        mapName: {
            type: String,
            required: false,
            default: 'trackingMap'
        },
        lat: {
            type: Number,
            required: false
        },
        lng: {
            type: Number,
            required: false
        },
        zoom: {
            type: Number,
            default: 10
        },
        parent: {
            type: String,
            required: false,
            default: null
        }
    },
    mounted () {
        this.initMap()
        this.$store.state.isOverlayActive = false
    },
    data () {
        return {
            map: null,
            platform: null,
            ui: null,
            group: null,
            isMapCenter: false,
            journeyData: null,
            currentRoute: null,
            mapObjects: {
                circles: [],
                markers: [],
                eventCircles: [],
                eventMarkers: []
            }
        }
    },
    provide () {
        return {
            map: () => this.map,
            platform: () => this.platform,
            ui: () => this.ui,
            group: () => this.group,
            journeyGroup: () => this.journeyGroup
        }
    },
    watch: {
        padding: function (newPadding) {
            this.viewPort.setPadding(newPadding.top || 0, newPadding.right || 0, newPadding.bottom || 0, newPadding.left || 0)
        },
        getMenuState: {
            handler () {
                setTimeout(() => {
                    this.viewPort.resize()
                }, 220)
            }
        },
        isTrackingLoading: {
            handler () {
                setTimeout(() => {
                    this.viewPort.resize()
                }, 220)
            }
        },
        centerCar: {
            handler (val) {
                if (val) {
                    this.singleVehicleTracking()
                }
            }
        }
    },
    computed: {
        ...mapState([
            'mapActiveFleets',
            'centerCarId'
        ]),
        ...mapGetters({
            getMenuState: 'layoutModule/getMenuState',
            isTrackingLoading: 'trackingModule/getIsLoading',
            centerCar: 'trackingModule/getCenterCar'
        })
    },
    methods: {
        initMap () {
            // eslint-disable-next-line no-undef
            this.group = new H.map.Group()

            // eslint-disable-next-line no-undef
            this.journeyGroup = new H.map.Group()

            // eslint-disable-next-line no-undef
            this.platform = new H.service.Platform({
                'apikey': process.env.VUE_APP_HERE_MAPS_API_KEY
            })

            let omv = this.platform.getOMVService()

            let style = new H.map.Style(`/map_yamls/normal.day.yaml`)

            let customLayer = omv.createLayer(style, {
                tileSize: 1 < devicePixelRatio ? 512 : 256
            })

            const defaultLayers = this.platform.createDefaultLayers({
                tileSize: devicePixelRatio > 1 ? 512 : 256,
            });

            defaultLayers.vector.normal.traffic.getProvider().setStyleInternal(new H.map.Style(`/map_yamls/flow.yaml`))

            // eslint-disable-next-line no-undef
            this.map = new H.Map(
                document.getElementById(this.mapName),
                customLayer,
                {
                    zoom: 10,
                    center: {
                        lat: this.lat || process.env.VUE_APP_HERE_MAPS_DEFAULT_LAT,
                        lng: this.lng || process.env.VUE_APP_HERE_MAPS_DEFAULT_LNG
                    },
                    pixelRatio: window.devicePixelRatio || 1
                }
            )

            this.map.setZoom(this.zoom)

            this.viewPort = this.map.getViewPort()
            this.viewPort.setPadding(this.padding.top || 0, this.padding.right || 0, this.padding.bottom || 0, this.padding.left || 0)

            window.addEventListener('resize', () => this.viewPort.resize())

            const customLayers = {
                baseLayers: [
                    {
                        label: 'Map',
                        layer: customLayer
                    },
                    {
                        label: 'Sattelite',
                        layer: defaultLayers.raster.satellite.map
                    },
                    {
                        label: 'Terrain',
                        layer: defaultLayers.raster.terrain.map
                    }
                ],
                layers: [
                    {
                        label: 'Incidents',
                        layer: defaultLayers.vector.normal.trafficincidents
                    },
                    {
                        label: 'Traffic',
                        layer: defaultLayers.vector.normal.traffic
                    }
                ]
            }

            this.ui = H.ui.UI.createDefault(this.map, customLayers)

            // eslint-disable-next-line no-undef
            this.ui.removeControl('mapsettings');

            const mapSettings = new H.ui.MapSettingsControl({
                baseLayers: [
                    {
                        label: 'Map',
                        layer: customLayer
                    },
                    {
                        label: 'Sattelite',
                        layer: defaultLayers.raster.satellite.map
                    },
                    {
                        label: 'Terrain',
                        layer: defaultLayers.raster.terrain.map
                    }
                ],
                layers: [
                    {
                        label: 'Incidents',
                        layer: defaultLayers.vector.normal.trafficincidents
                    },
                    {
                        label: 'Traffic',
                        layer: defaultLayers.vector.normal.traffic
                    }
                ]
            })

            this.ui.addControl('custom-mapsettings', mapSettings);

            const scalebarControl = this.ui.getControl('scalebar')

            if(scalebarControl) {
                scalebarControl.setAlignment(H.ui.LayoutAlignment.BOTTOM_RIGHT);
            }


            this.ui.getControl('zoom')
            this.ui.getControl('scalebar')
            // eslint-disable-next-line
            new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map))

            this.map.addObject(this.group)

            this.map.addEventListener('tap', (event) => {
                const bubbles = this.ui.getBubbles()
                if (!(event.target instanceof H.map.Marker)) {
                    bubbles.forEach(bub => {
                        bub.close()
                        this.ui.removeBubble(bub)
                    })
                }
            })

            this.$emit('setMapSettings', {
                map: this.map,
                ui: this.ui,
                platform: this.platform
            })
        },
        centerMap(handler = () => {
        }) {
            setTimeout(() => {
                if (process.env.VUE_APP_DEBUG) {
                    console.debug('CENTER MAP', this.map, this.group, this.group.getBoundingBox())
                }

                if (null == this.map || null == this.group) {
                    return
                }

                if (null == this.group.getBoundingBox()) {
                    return
                }

                this.map.getViewModel().setLookAtData({
                    bounds: this.group.getBoundingBox()
                })

                handler()
            }, 0)
        },
        singleVehicleTracking() {
            this.map.setCenter({
                lat: this.centerCar.vehicleTracking.lat,
                lng: this.centerCar.vehicleTracking.lng
            })
        }
    }
}
</script>

<style scoped lang="scss">
.map {
    width: 100%;
    height: calc(100vh - #{get($header-config, default, height)});
    position: relative;
    z-index: 1;

    &--event {
        height: 290px;
    }

    &--shared {
        height: 329px;
    }
}
</style>
